import { ElementRef, Injectable, Injector, OnDestroy } from '@angular/core';

import { BehaviorSubject,  Subscription } from 'rxjs';
import { isNull } from 'underscore';
import * as uuid from 'uuid';

import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { ContactModel } from '../model/contact.model';
import { OutletModel } from '../../../Discover/Targeting/models/outlet.model';
import { DialogIoModel } from '../../../Core/Dialogs/models/dialog-io.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileDialogComponent } from '../../RhpContexts/components/Entity/FullProfileDialog/full-profile-dialog.component';
import { TargetingRhpContextModel } from '../../RhpContexts/models/targeting-rhp-dialog-context.model';
import { RhpContextsTakeoverComponent } from '../../RhpContexts/components/RhpContextsTakeover/rhp-contexts-takeover.component';
import { TargetingRhpContextsService } from './targeting-rhp-contexts.service';
import { ContactService } from './contact-service';
import { DiscoverRoutingService } from '../../../Discover/DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteModel } from '../../../Discover/DiscoverRouter/models/discover-route.model';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { PrivateDataModel } from '../../../Discover/Targeting/models/private-data.model';
import {
    DiscoverRouteTypeEnumToFullProfileContextEnumMap, FullProfileContextEnum, FullProfileContextEnumToLocalizationTokenMap
} from '../../../Discover/Targeting/enums/full-profile-context.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { Title } from '@angular/platform-browser';
import { ProfileTakeOverInterface } from '../interfaces/profile-take-over.interface';


@Injectable()
export class TargetingRhpDialogService implements OnDestroy, ProfileTakeOverInterface {
    contextDialogs: TargetingRhpContextModel[] = [];
    subscriptions: Subscription[] = [];

    editingContact: boolean;
    backButtonLocalization: string;
    currentRoute: DiscoverRouteModel;

    titleHistory: {title: string; key: string}[] = [];
    changeTitleOnClose: boolean = true;

    constructor(
        public coreDialogService: CoreDialogService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public contactService: ContactService,
        public discoverRoutingService: DiscoverRoutingService,
        public localizationService: LocalizationService,
        public titleService: Title
    ) {
        this.subscriptions.push(
            this.contactService.getEditingContact().subscribe(editingContact => {
                this.editingContact = !!editingContact;

                if (editingContact) {
                    this.backButtonLocalization = FullProfileContextEnumToLocalizationTokenMap.get(FullProfileContextEnum.CONTACT);

                    return;
                }

                this.setBackButtonLocalizationFromRoute();
            }),
            this.discoverRoutingService.getCurrentRoute().subscribe(route => {
                this.currentRoute = route;
                this.setBackButtonLocalizationFromRoute();
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    setBackButtonLocalizationFromRoute(): void {
        if (!this.currentRoute || this.editingContact) {
            return;
        }

        // HACK because backButtonLocalization is only setup for discover routes
        if (isNull(this.currentRoute.type)) {
            this.backButtonLocalization = FullProfileContextEnumToLocalizationTokenMap.get(FullProfileContextEnum.SEARCH);
        } else {
            const fullProfileContext: FullProfileContextEnum = DiscoverRouteTypeEnumToFullProfileContextEnumMap.get(this.currentRoute.type);
            this.backButtonLocalization = FullProfileContextEnumToLocalizationTokenMap.get(fullProfileContext);
        }
    }

    openTakeOver(
        io?: DialogIoModel,
        injector?: Injector
    ) {
        const takeOverKey = this.coreDialogService.open(RhpContextsTakeoverComponent, io, injector);
        const contextModel = new TargetingRhpContextModel();
        contextModel.takeOverKey = takeOverKey;
        this.contextDialogs.push(contextModel);
    }

    openFullProfile(
        entityType: EntityType,
        contact?: ContactModel,
        outlet?: OutletModel,
        contactPrivateData?: PrivateDataModel,
        outletPrivateData?: PrivateDataModel,
        sidebarElement?: ElementRef,
        fullHeightProfileTakeover?: boolean
    ) {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        let contextModel;

        if (!this.contextDialogs.length) {
            contextModel = new TargetingRhpContextModel();
            this.contextDialogs.push(contextModel);
        } else {
            contextModel = this.contextDialogs[this.contextDialogs.length - 1];
        }

        const ioModel =  {
            inputs: {
                backButtonLocalization: this.backButtonLocalization,
                entityType: entityType,
                contact: contact,
                outlet: outlet,
                contactPrivateData: contactPrivateData,
                outletPrivateData: outletPrivateData,
                animationTrigger: animationTrigger,
                sidebarRef: sidebarElement,
                hideBackButton: false,
                fullHeight: fullHeightProfileTakeover
            },
            outputs: {
                dialogClose: (closeAction) => {
                    this.targetingRhpContextsService.closeTakeOver();
                    if (closeAction?.newSelectedEmploymentId) {
                        this.targetingRhpContextsService.changeSelectedEmployment(closeAction.newSelectedEmploymentId);
                    }
                    const contextToDelete = this.contextDialogs.find(context => context.fullProfileKey === closeAction?.fullProfileDialogKey);
                    this.contextDialogs = this.contextDialogs.filter(contextDialog => contextDialog !== contextToDelete);

                    if (this.changeTitleOnClose) {
                        const title = this.titleHistory.find(entry => entry.key === closeAction?.fullProfileDialogKey)?.title;
                        this.titleService.setTitle(title);
                    }

                    if (!this.coreDialogService.dialogComponentRefMap.size) {
                        this.titleHistory = [];
                    }

                },
                animationStateChange: (currentState) => {
                    if (currentState !== OverlayAnimationStateEnum.open) {
                        return;
                    }

                    if (contextModel.takeOverKey) {
                        this.coreDialogService.close(contextModel.takeOverKey);
                    }

                    if (this.contextDialogs.length === 2) {
                        this.deleteContext(this.contextDialogs[0]);
                    }
                }
            }
        };
        this.changeTitleOnClose = true;
        const fullProfileKey = this.coreDialogService.open(
            FullProfileDialogComponent,
            ioModel,
            undefined,
            // since we can open multiple dialogs of this type, we need to assign unique dialog key
            // before we had the contact or outlet uuid.. but since we can open up the "same" profile multiple times.. it was not unique
            uuid.v4() + '.fullProfileDialog'
        );
        contextModel.fullProfileKey = fullProfileKey;
        this.titleHistory.push({title: this.titleService.getTitle(), key: fullProfileKey});
        if (!!contact) {
            this.titleService.setTitle(
                this.localizationService.transform('Discover.contact.browserTitle', {first: contact.firstName, last: contact.lastName})
            );
        }
        if (!!outlet) {
            this.titleService.setTitle(
                this.localizationService.transform('Discover.outlet.browserTitle', {name: outlet.name})
            );
        }
    }

    deleteContext(context: TargetingRhpContextModel) {
        if (context.takeOverKey) {
            this.coreDialogService.close(context.takeOverKey);
        }

        if (context.fullProfileKey) {
            this.coreDialogService.close(context.fullProfileKey);
            // check the core dialog map for deleted entries from the title history and rearrange title history if needed
            const titlesToDelete = [];
            this.titleHistory.forEach((title, index) => {
                if (!this.coreDialogService.dialogComponentRefMap.get(title.key)) {
                    this.titleHistory[index + 1].title = title.title;
                    titlesToDelete.push(index);
                }
            });
            titlesToDelete.forEach(index => this.titleHistory.splice(index, 1));
        }
        this.contextDialogs = this.contextDialogs.filter(contextDialog => contextDialog !== context);
    }
}
